import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../store";
import SelectDropDown from "../SelectDropdown";
import Loader from "../Loader";
import {
  getLocatorFetchStatus,
  getLocatorsLengths,
  getLocatorsTypes,
} from "../../store/selectors/LocatorSelectors";
import {
  getLocatorsLengthsAction,
  getLocatorsTypesAction,
  createLocatorTypeAction,
  createLocatorLengthAction
} from "../../store/actions/LocatorsActions";

interface LocatorDropdownPropTypes {
  selectedLocatorType: string | undefined;
  selectedLocatorLength: string | undefined;
  onChooseLocatorType: (event: any) => void;
  onChooseLocatorLength: (event: any) => void;
}

const LocatorDropdownProps = (props: LocatorDropdownPropTypes) => {
  const {
    selectedLocatorType,
    selectedLocatorLength,
    onChooseLocatorType,
    onChooseLocatorLength,
  } = props;

  const dispatch = useAppDispatch();
  const locatorFetchStatus = useAppSelector(getLocatorFetchStatus);
  const locatorLengths = useAppSelector(getLocatorsLengths);
  const locatorTypes = useAppSelector(getLocatorsTypes);

  const isLocatorDataFetching = locatorFetchStatus === "loading";

  useEffect(() => {
    if (locatorLengths || isLocatorDataFetching) return;
    dispatch(getLocatorsLengthsAction());
  }, [locatorLengths, isLocatorDataFetching, dispatch]);

  useEffect(() => {
    if (locatorTypes || isLocatorDataFetching) return;
    dispatch(getLocatorsTypesAction());
  }, [locatorTypes, isLocatorDataFetching, dispatch]);

  let locatorTypeOptions: { value: string; label: string }[] = [];
  let locatorLengthsOptions: { value: string; label: string }[] = [];

  if (locatorTypes) {
    locatorTypeOptions = locatorTypes
      .filter((item) => item.typename !== null)
      .map(({ typeid, typename }) => ({
        value: typeid,
        label: typename,
      }));
  }

  if (locatorLengths) {
    locatorLengthsOptions = locatorLengths
      .filter((item) => item.length !== null)
      .map(({ lengthid, length }) => ({
        value: lengthid,
        label: length.toString(),
      }));
  }

  const createTextFilter = (filter: string) => {
    const filterExists = locatorTypes?.map(x => x.typename).includes(filter)
    if (!filterExists) {
      const formBody: any = [];
      formBody.push(encodeURIComponent('typename') + "=" + encodeURIComponent(filter))
      dispatch(createLocatorTypeAction(formBody.join("&")));
    }
  };

  const createNumberFilter = (filter: string) => {
    const filterAsFloat: number = parseFloat(filter) // Is NaN when it's not a float
    const filterExists = locatorLengths?.map(x => x.length).includes(filterAsFloat);

    if (filterAsFloat && !filterExists) {
      const formBody: any = [];
      formBody.push(encodeURIComponent('length') + "=" + encodeURIComponent(filterAsFloat))
      dispatch(createLocatorLengthAction(formBody.join("&")));
    }
  };

  const locatorTypesDropdown = locatorTypeOptions && (
    <div className="row bottom-30">
      <SelectDropDown
        hasCreateNewFilter={true}
        createFilter={createTextFilter}
        onChange={onChooseLocatorType}
        options={locatorTypeOptions}
        label="Locator Type"
        selectedOption={selectedLocatorType?.toUpperCase() || ""}
      />
    </div>
  );

  const locatorLengthDropdown = locatorLengthsOptions && (
    <div className="row bottom-30">
      <label>
        <SelectDropDown
          hasCreateNewFilter={true}
          createFilter={createNumberFilter}
          onChange={onChooseLocatorLength}
          options={locatorLengthsOptions}
          label="Locator Length (mm)"
          selectedOption={selectedLocatorLength?.toUpperCase() || ""}
        />
      </label>
    </div>
  );

  return isLocatorDataFetching ? (
    <Loader />
  ) : (
    <div className="locators-options">
      {locatorTypesDropdown}
      {locatorLengthDropdown}
    </div>
  );
};

export default LocatorDropdownProps;
